import React from 'react';
import ReactDOM from 'react-dom';
import { ApolloProvider, ApolloClient, HttpLink, InMemoryCache, ServerError, ServerParseError } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { FragmentConfig } from './fragments/types';

import { getFragmentByType } from './fragments/getFragmentByType';
import { EventSystemProvider } from './components/EventProvider/EventProvider';
import { IntlProvider } from 'react-intl';

async function loadLanguageFile(lang: string): Promise<any> {
  try {
    return await import(`./lang/${lang}.json`);
  } catch (e) {
    return await import(/* webpackChunkName: "en-US" */ './lang/en-US.json');
  }
}

interface RenderProps {
  apiKey: string;
  authFn?: () => Promise<string>;
  authToken?: string;
  config: FragmentConfig;
  domElm: HTMLElement;
  lang: string;
  signal: AbortSignal;
}

interface AuthHeaders {
  'Accept-Language'?: string;
  Authorization?: string;
  'x-els-fs-consumerkey'?: string;
}

const graphqlUri = process.env.REACT_APP_API_ENDPOINT;

export function createApolloClient({ authFn, headers }: { authFn?: RenderProps['authFn']; headers: AuthHeaders }): any {
  const authLink = setContext(async () => {
    if (authFn) {
      const token = await authFn();
      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      };
    }

    return { headers };
  });

  const errorLink = onError(({ operation, networkError }) => {
    if (networkError && (networkError as ServerError | ServerParseError).statusCode === 401) {
      const { headers } = operation.getContext();
      const token = headers?.Authorization.replace('Bearer ', '') ?? '';
      if (token) {
        const tokenBodyDecoded = JSON.parse(window.atob(token.split('.')[1]));
        if (Math.floor(Date.now() / 1000) > tokenBodyDecoded.exp) {
          networkError.message = 'Token has expired';
        }
      }
    }
  });

  const httplink = new HttpLink({ uri: graphqlUri });
  return new ApolloClient({
    link: errorLink.concat(authLink.concat(httplink)),
    cache: new InMemoryCache(),
  });
}

const lang = process.env.REACT_APP_LANG || 'en-US';
const authHeaders = {
  'Accept-Language': lang,
  Authorization: `Bearer ${process.env.REACT_APP_TOKEN}`,
  'x-els-fs-consumerkey': process.env.REACT_APP_LOCAL_API_KEY,
};
export const apolloClientForLocalDev = createApolloClient({
  headers: authHeaders,
});

if (process.env.NODE_ENV === 'development') {
  if (process.env.REACT_APP_LOCAL_MOCK) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { worker } = require('./mocks/browser');
    worker.start();
  }

  [
    'addGrant:undo',
    'addGrant',
    'chartPointClick',
    'chartPointClick',
    'clickFacet',
    'clickFunderLead',
    'clickGrantId',
    'clickInstitution',
    'clickListItem',
    'clickListItem',
    'clickOverviewMeasure',
    'clickResearcher',
    'clickRow',
    'clickShowFullDescription',
    'clickSummary',
    'errorThrown',
    'lastUpdate',
    'removeGrant:undo',
    'removeGrant',
    'templatedDetails',
    'totalRowCount',
  ].forEach((eventSuffix) => {
    window.addEventListener(`fragment:${eventSuffix}`, ((e: CustomEvent) => console.log(e)) as EventListener);
  });

  // Todo: Create env variable or a dropdown to choose the available mock configs.
  // eslint-disable-next-block @typescript-eslint/no-var-requires
  const configs: FragmentConfig[] = [
    require('./mockConfigs/chart.config.json'),
    // require('./mockConfigs/entityList.config.json'),
    // require('./mockConfigs/FI.templated.config1.json'),
    // require('./mockConfigs/FI.templated.config3.json'),
    // require('./mockConfigs/FI.templated.config4.json'),
    // require('./mockConfigs/FI.scatter.config.json'),
    // require('./mockConfigs/FI.table.config.json'),
    // require('./mockConfigs/grantDetails.config.json'),
    // require('./mockConfigs/publicationsList.config.json'),
    // require('./mockConfigs/termsOfUse.config.json'),
    // require('./mockConfigs/map.config.json'),
    // require('./mockConfigs/multipleAxis.config.json'),
    // require('./mockConfigs/overview.config.json'),
    require('./mockConfigs/summary.config.json'),
    // require('./mockConfigs/table.config.json'),
    // require('./mockConfigs/tableOverYears.config.json'),
    // require('./mockConfigs/templated.config.json'),
    // require('./mockConfigs/hydraMetrics.config.json'),
  ];

  //Todo: To be handle once we have start handling multiple visualisations.

  const rootElement = document.getElementById('root');

  if (!rootElement) throw new Error('Missing rootElement');

  loadLanguageFile(lang).then((messages) => {
    ReactDOM.render(
      <React.StrictMode>
        <EventSystemProvider domElm={rootElement} fragmentName="fragment">
          <IntlProvider defaultLocale="en-US" locale={lang} messages={messages}>
            <ErrorBoundary>
              <ApolloProvider client={apolloClientForLocalDev}>
                <React.Suspense fallback={null}>
                  {configs.map((config: FragmentConfig, index: number) => {
                    const Fragment = getFragmentByType(config.visualisations[0].type);
                    config.fragmentName = 'fragment';
                    return (
                      <section key={`fragment_${index}`} style={{ paddingBottom: '2em' }}>
                        <h1>
                          Fragment {index + 1} - {config.visualisations[0].type}
                        </h1>
                        <Fragment config={config} />
                      </section>
                    );
                  })}
                </React.Suspense>
              </ApolloProvider>
            </ErrorBoundary>
          </IntlProvider>
        </EventSystemProvider>
      </React.StrictMode>,
      rootElement,
    );
  });
} else {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  document.currentScript?.loadFragment?.(
    async ({ apiKey, authFn, authToken, config, domElm, lang = 'en-US', signal }: RenderProps): Promise<void> => {
      const messages = await loadLanguageFile(lang);

      const authHeaders = {
        'Accept-Language': lang,
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
        'x-els-fs-consumerkey': apiKey,
      };
      const fragmentName = config.fragmentName || domElm?.getAttribute('name') || 'fragment-factory';
      const client = createApolloClient({
        authFn,
        headers: authHeaders,
      });

      const Fragment = getFragmentByType(config.visualisations[0].type);

      ReactDOM.render(
        <React.StrictMode>
          <EventSystemProvider domElm={domElm} fragmentName={fragmentName}>
            <IntlProvider defaultLocale="en-US" locale={lang} messages={messages}>
              <ErrorBoundary>
                <ApolloProvider client={client}>
                  <React.Suspense fallback={null}>
                    <Fragment config={config} />
                  </React.Suspense>
                </ApolloProvider>
              </ErrorBoundary>
            </IntlProvider>
          </EventSystemProvider>
        </React.StrictMode>,
        domElm,
      );

      signal.addEventListener(
        'abort',
        () => {
          ReactDOM.unmountComponentAtNode(domElm);
        },
        { once: true },
      );
    },
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log);